import { Link, useNavigate } from 'react-router-dom';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { useEstimateMetrics } from '../hooks/useEstimateMetrics';
import { useEstimates } from '../hooks/useEstimates';
import { formatCurrency, formatSolutionType, formatDate } from '../lib/formatters';
import type { EstimateListRow } from '../lib/estimateTypes';

export function DashboardPage() {
  const { data: metrics, isLoading, error: metricsError } = useEstimateMetrics();
  const { data: allEstimates, error: listError } = useEstimates();
  const navigate = useNavigate();

  const estimates = allEstimates ?? [];
  const activeEstimates = estimates.filter((e) => e.status === 'draft' || e.status === 'sent');
  const replies = estimates.filter((e) => e.m365_last_reply_at);
  const ninetyDaysAgo = new Date();
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
  const accepted = estimates.filter(
    (e) => e.status === 'accepted' && new Date(e.updated_at) >= ninetyDaysAgo,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading estimates</span>
      </div>
    );
  }

  const loadError = metricsError ?? listError;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-text">Estimator</h1>
        <Link
          to="/estimates/new"
          className="inline-flex min-h-11 items-center px-4 py-2 text-sm font-medium rounded-lg bg-primary text-text-inverse hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          New estimate
        </Link>
      </div>

      {loadError && (
        <p className="text-sm text-error" role="alert">
          {loadError instanceof Error ? loadError.message : 'Estimates could not be loaded.'}
        </p>
      )}

      {/* Metric cards — every number is a count over estimate rows */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Drafts" value={metrics ? String(metrics.inProgress) : '—'} to="/proposals?status=draft" />
        <Card label="Sent proposals" value={metrics ? String(metrics.sent) : '—'} to="/proposals?status=sent" />
        <Card
          label="Awaiting reply"
          value={metrics ? String(metrics.awaitingReply) : '—'}
          alert={(metrics?.awaitingReply ?? 0) > 0}
          to="/mailbox"
        />
        <Card
          label="Pipeline value"
          value={metrics ? formatCurrency(metrics.pipelineValue) : '—'}
          hint="Draft + sent"
          to="/proposals"
        />
      </div>

      {/* Active estimates */}
      <section className="bg-surface rounded-lg border border-border p-4 sm:p-5" aria-labelledby="active-heading">
        <h2 id="active-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-4">Active estimates</h2>
        <div className="space-y-2">
          {activeEstimates.length === 0 && (
            <EmptyState
              icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
              title="No active estimates"
              subtitle="Draft and sent estimates will show here."
              action={{ label: 'New estimate', onClick: () => navigate('/estimates/new') }}
            />
          )}
          {activeEstimates.map((e: EstimateListRow) => (
            <Link
              key={e.id}
              to={`/estimates/${e.id}`}
              className="w-full flex items-center justify-between gap-3 min-h-11 p-3 rounded-md bg-surface-2 hover:bg-surface-strong text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-text truncate">
                  {e.customer?.name ?? e.contact_name ?? 'No customer'} {'—'} {e.location?.name ?? 'No site'}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="text-xs font-mono text-text-faint">{e.estimate_number}</span>
                  {e.solution_type && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary-soft text-primary">{formatSolutionType(e.solution_type)}</span>
                  )}
                  <span className="text-xs text-text-faint">{formatDate(e.created_at)}</span>
                </div>
              </div>
              <StatusBadge status={e.status ?? 'draft'} />
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-surface rounded-lg border border-border p-4 sm:p-5" aria-labelledby="mailbox-heading">
        <div className="flex items-center justify-between mb-3">
          <h2 id="mailbox-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider">Mailbox</h2>
          <Link to="/mailbox" className="inline-flex min-h-11 items-center text-xs font-medium text-primary hover:underline">Open mailbox</Link>
        </div>
        {replies.length === 0 ? (
          <p className="text-sm text-text-muted">No proposal replies recorded on any estimate.</p>
        ) : (
          <div className="space-y-1.5">
            {replies.slice(0, 5).map((e) => (
              <Link
                key={e.id}
                to={`/estimates/${e.id}`}
                className="block w-full text-left min-h-11 p-3 rounded-md bg-surface-2 hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <p className="text-sm font-medium text-text">{e.customer?.name ?? e.contact_name ?? e.estimate_number}</p>
                <p className="text-xs text-text-muted mt-0.5">Last reply {formatDate(e.m365_last_reply_at)}</p>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Accepted proposals */}
      <section className="bg-surface rounded-lg border border-border p-4 sm:p-5" aria-labelledby="accepted-heading">
        <h2 id="accepted-heading" className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-4">Accepted proposals (90 days)</h2>
        <div className="space-y-2">
          {accepted.length === 0 && <p className="text-sm text-text-muted">No accepted proposals in the last 90 days.</p>}
          {accepted.map((e) => (
            <Link
              key={e.id}
              to={`/estimates/${e.id}`}
              className="w-full flex items-center justify-between gap-3 min-h-11 p-3 rounded-md bg-success-soft hover:bg-surface-strong text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-text truncate">{e.customer?.name ?? e.contact_name ?? e.estimate_number}</p>
                <span className="text-xs text-text-muted">{formatDate(e.updated_at)}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status="accepted" />
                <span className="text-sm font-semibold text-success">{formatCurrency(e.total_cents ?? 0)}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function Card({ label, value, hint, alert, to }: { label: string; value: string; hint?: string; alert?: boolean; to: string }) {
  return (
    <Link
      to={to}
      className="block bg-surface rounded-lg border border-border p-4 sm:p-5 text-left hover:bg-surface-strong transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${alert ? 'text-warning' : 'text-text'}`}>{value}</p>
      {hint && <p className="text-[11px] text-text-faint mt-0.5">{hint}</p>}
    </Link>
  );
}
